import {
    Alert,
    Autocomplete,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    List,
    ListItemButton,
    ListItemText,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useVideoProcessing } from '../../contexts/VideoProcessingContext';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import { validateItemName } from '../../utils/formatUtils';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
// Only the list: its component writes the languages New video reads, so it isn't used here
import { flatLangOptions } from '../subtitles/SelectLanguageComponent';
import { driveLinkError, isDriveLink } from './driveLink';
import { formatFileSize, shortName, startVideoLanguages } from './taskUi';
import { withAccessConfirmation } from './confirmAccess';

const describeFile = (f) =>
    [f.folder, formatFileSize(f.size), shortName(f.uploadedBy), new Date(f.uploadedAt).toLocaleDateString()]
        .filter(Boolean)
        .join(' · ');

const FOLDER = 'folder';
const DRIVE = 'drive';

/**
 * Start video: makes the waiting task's video from a file in its shared folder or a Google Drive
 * link. The name stays as picked here; bit rate, CRF and translation options are New video's defaults.
 * again: the video failed, so this only asks where to run the same video from, starting on the
 * source the last run used. startedVideo: the task detail's, for that run.
 * onStarted gets the result, with keepOpen when some languages didn't link.
 */
const StartVideoDialog = ({ open, task, plan, again, startedVideo, onClose, onStarted }) => {
    useTrackUsage('StartVideoDialog', 'dialog', 'tasks', open);
    const { trackVideo } = useVideoProcessing();
    const [files, setFiles] = useState(null);
    const [fileId, setFileId] = useState(null);
    const [source, setSource] = useState(null);
    const [link, setLink] = useState('');
    const [linkLeft, setLinkLeft] = useState(false);
    const [linkRefused, setLinkRefused] = useState(null);
    const [name, setName] = useState('');
    const [folder, setFolder] = useState('/');
    const [choosingFolder, setChoosingFolder] = useState(false);
    const [extra, setExtra] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const contentId = again ? task?.contentId : plan?.planId;
    const { fixed, addable } = startVideoLanguages(plan?.waitingLanguages, flatLangOptions);
    const lastLink = again ? startedVideo?.sourceLink : null;
    const lastFromLink = again && !!startedVideo?.fromDriveLink;

    useEffect(() => {
        if (!open || !contentId) return undefined;
        let alive = true;
        setFiles(null);
        setFileId(null);
        setSource(null);
        setLink(lastLink || (isDriveLink(task?.sourceUrl) ? task.sourceUrl : ''));
        setLinkLeft(false);
        setLinkRefused(null);
        setName(task?.contentTitle || '');
        setFolder('/');
        setExtra([]);
        setError(null);
        TaskTrackingService.videoFiles(contentId)
            .then((found) => {
                if (!alive) return;
                setFiles(found);
                if (found.length === 1) setFileId(found[0].id);
                setSource(lastFromLink || found.length === 0 ? DRIVE : FOLDER);
            })
            .catch((e) => {
                if (!alive) return;
                setError(taskErrorMessage(e));
                setFiles([]);
                setSource(DRIVE);
            });
        return () => {
            alive = false;
        };
    }, [open, contentId, task?.contentTitle, task?.sourceUrl, lastLink, lastFromLink]);

    const fromDrive = source === DRIVE;
    const typedLink = link.trim();
    const linkMistake = driveLinkError(link);
    const linkError = linkRefused || (linkLeft ? linkMistake : null);
    // On Start video again a task still on the old link moves with it, so only a typed one stays
    const taskKeepsItsLink =
        !!task?.sourceUrl && !!typedLink && task.sourceUrl !== typedLink && !(again && task.sourceUrl === lastLink);
    const sourceReady = fromDrive ? !!typedLink && !linkMistake : !!fileId;
    const nameError = again ? null : validateItemName(name, 'video');
    const submit = async () => {
        setBusy(true);
        setError(null);
        setLinkRefused(null);
        const chosen = fromDrive ? { driveLink: typedLink } : { fileId };
        try {
            const result = again
                ? await TaskTrackingService.startVideoAgain(task.contentId, chosen)
                : await withAccessConfirmation((grant) =>
                      TaskTrackingService.startVideo(plan.planId, {
                          ...chosen,
                          name: name.trim(),
                          folder,
                          languages: extra.map((o) => o.label),
                          grantContentAccess: grant,
                      }),
                  );
            if (!result) return;
            trackVideo(result.videoId, `${result.name} - ${fromDrive ? 'From Google Drive' : 'New Video Upload'}`);
            onStarted(result);
        } catch (e) {
            if (e?.response?.data?.code === 'DRIVE_LINK') setLinkRefused(taskErrorMessage(e));
            else setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{again ? 'Start video translation again' : 'Start video translation'}</DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {error}
                        </Alert>
                    )}
                    <DialogContentText variant="body2">
                        {again
                            ? `The same video runs again, so its tasks stay as they are.${fromDrive ? '' : ' Pick another file if this one is damaged.'}`
                            : `This makes the video in Files and starts every language of this task.${fromDrive ? '' : ' The file stays in the shared folder.'}`}
                    </DialogContentText>
                    <FormControl sx={{ mt: 1 }}>
                        <FormLabel id="start-video-source">Source</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="start-video-source"
                            value={source ?? ''}
                            onChange={(e) => setSource(e.target.value)}
                        >
                            <FormControlLabel value={FOLDER} control={<Radio size="small" />} label="Shared folder" />
                            <FormControlLabel value={DRIVE} control={<Radio size="small" />} label="Google Drive link" />
                        </RadioGroup>
                    </FormControl>
                    {source === FOLDER && (
                        <>
                            <Typography variant="overline" component="div" sx={{ mt: 1 }}>
                                File
                            </Typography>
                            {files?.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    The shared folder has no video now.
                                </Typography>
                            )}
                            {files?.length > 0 && (
                                <List dense aria-label="Video files" sx={{ py: 0 }}>
                                    {files.map((f) => (
                                        <ListItemButton key={f.id} selected={fileId === f.id} onClick={() => setFileId(f.id)}>
                                            <ListItemText primary={f.name} secondary={describeFile(f)} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            )}
                        </>
                    )}
                    {fromDrive && (
                        <>
                            <TextField
                                fullWidth
                                autoFocus
                                margin="dense"
                                label="Google Drive link"
                                value={link}
                                onChange={(e) => {
                                    setLink(e.target.value);
                                    setLinkRefused(null);
                                }}
                                onBlur={() => setLinkLeft(true)}
                                error={!!linkError}
                                helperText={
                                    linkError ? (
                                        <span role="alert">{linkError}</span>
                                    ) : (
                                        'The file must be shared as Anyone with the link. The video stays on Drive. A large file can take hours.'
                                    )
                                }
                            />
                            {taskKeepsItsLink && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                    This task's Source video link stays as it is.
                                </Typography>
                            )}
                        </>
                    )}
                    {!again && (
                        <>
                            <TextField
                                fullWidth
                                margin="dense"
                                label="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                error={!!nameError}
                                helperText={nameError}
                            />
                            <Typography variant="overline" component="div" sx={{ mt: 1 }}>
                                Folder in Files
                            </Typography>
                            <Button size="small" variant="outlined" onClick={() => setChoosingFolder(true)}>
                                {folder}
                            </Button>
                            <Typography variant="overline" component="div" sx={{ mt: 1 }}>
                                Languages
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                                These always go on the video.
                            </Typography>
                            {fixed.map((language) => (
                                <Chip key={language} size="small" label={language} sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                            <Autocomplete
                                multiple
                                size="small"
                                sx={{ mt: 1 }}
                                options={addable}
                                value={extra}
                                isOptionEqualToValue={(a, b) => a.value === b.value}
                                onChange={(e, value) => setExtra(value)}
                                renderInput={(params) => <TextField {...params} label="Also add to the video (no task)" />}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" disabled={!sourceReady || !!nameError || busy} onClick={submit}>
                        {busy && fromDrive ? 'Checking link...' : again ? 'Start again' : 'Start'}
                    </Button>
                </DialogActions>
            </Dialog>
            <PathSelectorDialog
                open={choosingFolder}
                onClose={() => setChoosingFolder(false)}
                onSelect={(path) => {
                    setFolder(path);
                    setChoosingFolder(false);
                }}
                currentPath={folder}
                title="Select Destination Folder"
            />
        </>
    );
};

export default StartVideoDialog;
